import { useEffect, useRef, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Lenis from "lenis";
import { toast, Toaster } from "sonner";
import "@/App.css";
import { CONTENT } from "@/content";
import { Header, BottomNav, Marquee } from "@/components/deck/Chrome";
import Hero from "@/components/deck/Hero";
import {
  LocationSection, ProjectSection, LifestyleSection,
  VillaSection, CustomSection, MasterplanSection, ContactSection,
} from "@/components/deck/BuyerSections";
import { InvestorGate, InvestorDashboard } from "@/components/deck/Investor";
import { exportDeckPdf } from "@/utils/pdfExport";
import Admin from "@/pages/Admin";

const SECTION_IDS = ["cover", "location", "project", "lifestyle", "villa", "custom", "masterplan", "contact", "investors"];

function Deck() {
  const [lang, setLang] = useState(() => {
    const p = new URLSearchParams(window.location.search).get("lang");
    return ["en", "ar"].includes(p) ? p : "it";
  });
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem("investor_unlocked") === "1");
  const [activeIndex, setActiveIndex] = useState(0);
  const [exporting, setExporting] = useState(false);
  const lenisRef = useRef(null);
  const t = CONTENT[lang];

  useEffect(() => {
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
    return () => {
      document.documentElement.dir = "ltr";
    };
  }, [lang]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.3, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const hash = window.location.hash.slice(1);
    if (hash) {
      setTimeout(() => {
        const el = document.getElementById(hash);
        if (el) lenis.scrollTo(el, { duration: 1.6 });
      }, 700);
    }
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const idx = SECTION_IDS.indexOf(e.target.id);
            if (idx >= 0) setActiveIndex(idx);
          }
        });
      },
      { threshold: 0.35 }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [unlocked]);

  const scrollToId = useCallback((id) => {
    const el = document.getElementById(id);
    if (el && lenisRef.current) lenisRef.current.scrollTo(el, { offset: 0, duration: 1.4 });
  }, []);

  const goto = (dir) => {
    const next = Math.min(Math.max(activeIndex + dir, 0), SECTION_IDS.length - 1);
    scrollToId(SECTION_IDS[next]);
  };

  const handlePdf = async () => {
    if (exporting) return;
    setExporting(true);
    toast(lang === "it" ? "Generazione PDF in corso…" : lang === "ar" ? "جارٍ إنشاء ملف PDF…" : "Generating PDF…");
    try {
      await exportDeckPdf(t, lang, unlocked);
    } finally {
      setExporting(false);
    }
  };

  const handleShare = async () => {
    const sec = SECTION_IDS[activeIndex];
    const url = `${window.location.origin}/?lang=${lang}${sec !== "cover" ? `#${sec}` : ""}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(lang === "it" ? "Link copiato negli appunti" : lang === "ar" ? "تم نسخ الرابط" : "Link copied to clipboard");
    } catch {
      toast(url);
    }
  };

  const handleUnlock = () => {
    sessionStorage.setItem("investor_unlocked", "1");
    setUnlocked(true);
    toast.success(lang === "it" ? "Accesso concesso. Benvenuto." : lang === "ar" ? "تم منح الدخول. مرحبًا." : "Access granted. Welcome.");
    setTimeout(() => scrollToId("investment-case"), 300);
  };

  return (
    <div className="App bg-[#0a0c0b] text-[#f2f0e9]">
      <div className="noise-overlay" />
      <Toaster theme="dark" position="top-center" />
      <Header lang={lang} setLang={setLang} t={t} onPdf={handlePdf} onShare={handleShare} onInvestors={() => scrollToId("investors")} />

      <main>
        <Hero t={t} onCta1={() => scrollToId("location")} onCta2={() => scrollToId("investors")} />
        <Marquee text={t.marquee} />
        <LocationSection t={t} />
        <ProjectSection t={t} />
        <LifestyleSection t={t} />
        <VillaSection t={t} />
        <CustomSection t={t} />
        <MasterplanSection t={t} />
        <ContactSection t={t} lang={lang} />
        {unlocked ? <InvestorDashboard t={t} lang={lang} /> : <InvestorGate t={t} lang={lang} onUnlock={handleUnlock} />}
      </main>

      <footer className="border-t border-white/10 py-10 px-6 md:px-12 pb-24">
        <p className="text-xs text-[#8c877d] max-w-2xl leading-relaxed" data-testid="deck-footer">{t.footer}</p>
      </footer>

      <BottomNav index={activeIndex} total={SECTION_IDS.length} onPrev={() => goto(-1)} onNext={() => goto(1)} />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Deck />} />
        <Route path="/admin" element={<Admin />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
